/**
 * Local fetch-based service for the dev-portal home page's real telemetry
 * summary. Does NOT use the xeppt-sdk — this endpoint is not in the
 * published SDK yet (same situation as webhook_service.ts).
 *
 * AUTH: Bearer access token from localStorage 'client-metadata', same
 * pattern as webhook_service.ts, merchant_config_service.ts, etc.
 *
 * API CONTRACT:
 *   GET /developers/home-summary[?environment=test|live] — business-scoped
 *   (403 for non-business accounts), fixed 30-day window. `environment` is
 *   optional (from the dev-portal's Sandbox/Live pill); omitted entirely
 *   when not passed.
 *     → { currency, period_days, today, last_30_days, previous_30_days,
 *         daily_volume, api_requests, status }
 *
 *   `currency` is the merchant's real wallet currency — callers must use it
 *   to render amounts, never assume "$"/CAD.
 *   `*.approved_amount` is decimal dollars (NOT cents).
 *   `*.success_rate` is a 0..1 fraction, null when there have been no
 *   terminal (approved+failed) attempts yet in that window — render that as
 *   an honest "no data" state, not 0%.
 *   `previous_30_days` is the SAME shape as `last_30_days`, for the 30-day
 *   window immediately before it — the only valid baseline for period-over-
 *   period deltas (never diff against `today`, which is a different window
 *   size).
 *   `daily_volume` is a dense 30-entry series, oldest first.
 *   `api_requests` covers raw API request volume, which the approved/failed
 *   period stats do NOT capture (those are payment-attempt outcomes, not
 *   request counts). `api_requests.daily` is a dense 30-entry series, oldest
 *   first, same convention as `daily_volume`.
 *   `previous_30_days` and `api_requests` are the newest blocks and are
 *   OPTIONAL on the wire: the backend deploys independently of the portal, so
 *   an older backend can omit them. Callers MUST treat absence as "no data
 *   yet" and degrade gracefully, never crash.
 *   `status` reflects the API's own health only, not third-party processors.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? '';

function authHeaders(): Record<string, string> {
    try {
        const raw = localStorage.getItem('client-metadata');
        if (!raw) return {};
        const meta = JSON.parse(raw) as { accessToken?: string };
        return meta?.accessToken ? { Authorization: `Bearer ${meta.accessToken}` } : {};
    } catch {
        return {};
    }
}

// ─── Types ───────────────────────────────────────────────────────────────────

export interface HomeSummaryPeriodStats {
    approved_amount: number;
    approved_count: number;
    failed_count: number;
    /** 0..1 fraction; null when there have been no terminal attempts yet. */
    success_rate: number | null;
}

export interface HomeSummaryDailyVolume {
    date: string;
    approved_amount: number;
    approved_count: number;
}

export interface ApiRequestsDailyCount {
    date: string;
    count: number;
}

export interface ApiRequestsSummary {
    today: number;
    last_30_days: number;
    /** Dense 30-entry series, oldest first — same convention as `daily_volume`. */
    daily: ApiRequestsDailyCount[];
}

export interface HomeSummary {
    /** The merchant's real wallet currency (e.g. "CAD") — use for all money display. */
    currency: string;
    period_days: number;
    today: HomeSummaryPeriodStats;
    /** A superset of `today` — do not sum the two. */
    last_30_days: HomeSummaryPeriodStats;
    /**
     * The 30-day window immediately before `last_30_days` — same shape, used
     * as the baseline for real period-over-period deltas. Do not diff
     * against `today` (different window size) or treat a zero/absent value
     * here as anything other than "no baseline yet" (e.g. a merchant with
     * under 60 days of history).
     *
     * OPTIONAL: the backend rolls out independently of the portal, so a
     * portal build that expects this block can receive an older response that
     * predates it. Callers MUST treat an absent block as "no baseline yet"
     * (render the honest "Last 30 days" label, never a crash).
     */
    previous_30_days?: HomeSummaryPeriodStats;
    daily_volume: HomeSummaryDailyVolume[];
    /**
     * Raw API request volume — distinct from the payment-outcome counts above.
     *
     * OPTIONAL for the same version-skew reason as `previous_30_days`: an
     * older backend omits it. Callers MUST render an honest zero/empty state
     * when it's absent, never a crash.
     */
    api_requests?: ApiRequestsSummary;
    /**
     * Typed as `string` (not a literal) even though today the backend only
     * ever returns "operational" — callers must compare with
     * `=== 'operational'` and treat every other value as degraded, so future
     * backend values (e.g. "degraded", "down") are handled without a type change here.
     */
    status: string;
}

// ─── Error class ─────────────────────────────────────────────────────────────

export class HomeSummaryApiError extends Error {
    constructor(
        message: string,
        public readonly status: number,
        public readonly code?: string,
        options?: ErrorOptions
    ) {
        super(message, options);
        this.name = 'HomeSummaryApiError';
    }
}

async function throwOnError(response: Response): Promise<never> {
    let code: string | undefined;
    let message: string | undefined;
    try {
        const errBody = (await response.json()) as { code?: string; message?: string };
        code = typeof errBody?.code === 'string' ? errBody.code : undefined;
        message = typeof errBody?.message === 'string' ? errBody.message : undefined;
    } catch {
        // ignore JSON parse failures on error bodies
    }
    throw new HomeSummaryApiError(message ?? `HTTP ${response.status}`, response.status, code);
}

async function parseResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
        await throwOnError(response);
    }
    return response.json() as Promise<T>;
}

// ─── Service ────────────────────────────────────────────────────────────────

export const apiHomeSummaryService = {
    /**
     * GET /developers/home-summary — the merchant's real 30-day telemetry:
     * approved volume/count, failed count and success rate for today and the
     * trailing 30 days, a dense daily volume series, and API health status.
     * `environment` (from the dev-portal's Sandbox/Live pill) is optional —
     * appended as ?environment=<value> when passed, omitted entirely
     * otherwise.
     */
    async getSummary(environment?: 'test' | 'live'): Promise<HomeSummary> {
        const qs = environment ? `?environment=${environment}` : '';
        const response = await fetch(`${BACKEND_URL}/developers/home-summary${qs}`, {
            headers: authHeaders()
        });
        return parseResponse<HomeSummary>(response);
    }
};
